import { ReactNode, useEffect } from "react";
import { Link } from "react-router";
import { useCurrentUser } from "../../hooks/useAPI";
import { Shield, AlertTriangle } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
  requireEmailVerified?: boolean;
  /**
   * @deprecated The mandatory workspace-onboarding wizard was removed
   * 2026-05-02. The prop is kept for callsite compatibility but does
   * nothing — every authenticated user can access the dashboard
   * directly. Agent onboarding lives at /dashboard/agents/new and is
   * NOT a gate.
   */
  requireOnboarding?: boolean;
  requiredRole?: "admin" | "member" | "viewer";
  fallback?: ReactNode;
  onUnauthorized?: () => void;
  onEmailNotVerified?: () => void;
}

export function ProtectedRoute({
  children,
  requireEmailVerified = true,
  // requireOnboarding intentionally accepted-but-ignored — see prop docs.
  requireOnboarding: _requireOnboarding,
  requiredRole,
  fallback,
  onUnauthorized,
  onEmailNotVerified,
}: ProtectedRouteProps) {
  void _requireOnboarding;
  const { data: user, isLoading, error } = useCurrentUser();

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!isLoading && (!user || error)) {
      onUnauthorized?.();
      // window.location.href = '/login';
    }
  }, [user, isLoading, error, onUnauthorized]);

  useEffect(() => {
    // Redirect to email verification if required
    if (!isLoading && user && requireEmailVerified && !user.emailVerified) {
      onEmailNotVerified?.();
      // window.location.href = '/verify-email';
    }
  }, [user, isLoading, requireEmailVerified, onEmailNotVerified]);

  // Onboarding gate removed 2026-05-02: workspace-onboarding wizard was
  // deleted, every authenticated + email-verified user goes straight
  // to the dashboard.

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 rounded-full animate-spin mx-auto" style={{ border: '2px solid transparent', borderTopColor: 'var(--brand)' }} />
          <div>
            <p className="text-sm text-mono text-foreground font-semibold">ADJUDON</p>
            <p className="text-xs text-muted-foreground mt-1">Authenticating...</p>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user || error) {
    if (fallback) return <>{fallback}</>;
    
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <div className="adp-card text-center">
            <div className="w-16 h-16 bg-surface-2 border border-border flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Authentication Required
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              You must be logged in to access this page.
            </p>
            <Link
              to="/login"
              className="inline-flex px-6 py-3 adp-btn-primary text-sm font-semibold transition-colors"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Email not verified
  if (requireEmailVerified && !user.emailVerified) {
    if (fallback) return <>{fallback}</>;
    
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <div className="adp-card text-center">
            <div className="w-16 h-16 bg-surface-2 border border-border flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Email Verification Required
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Please verify your email address to continue.
            </p>
            <Link
              to="/verify-email"
              className="inline-flex px-6 py-3 adp-btn-primary text-sm font-semibold transition-colors"
            >
              Verify Email
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // (Onboarding gate removed — see prop docs.)

  // Role check
  if (requiredRole && user.role !== requiredRole) {
    // Allow admin to access everything
    if (user.role !== "admin") {
      if (fallback) return <>{fallback}</>;
      
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-8">
          <div className="max-w-md w-full">
            <div className="adp-card text-center">
              <div className="w-16 h-16 bg-surface-2 border border-border flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Insufficient Permissions
              </h2>
              <p className="text-sm text-muted-foreground mb-2">
                You don't have permission to access this page.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                Required role: <span className="text-mono text-foreground">{requiredRole}</span>
                <br />
                Your role: <span className="text-mono text-foreground">{user.role}</span>
              </p>
              <Link
                to="/dashboard"
                className="inline-flex px-6 py-3 bg-surface-1 hover:bg-surface-2 border border-border text-foreground text-sm font-medium transition-colors"
              >
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      );
    }
  }

  // All checks passed - render protected content
  return <>{children}</>;
}

// ============================================================================
// Convenience Hooks
// ============================================================================

export function useAuth() {
  const { data: user, isLoading, error } = useCurrentUser();

  return {
    user,
    isLoading,
    isAuthenticated: !!user && !error,
    isEmailVerified: user?.emailVerified ?? false,
    /** @deprecated Always true — workspace-onboarding gate removed 2026-05-02. */
    isOnboardingCompleted: true,
    role: user?.role,
    isAdmin: user?.role === "admin",
    isMember: user?.role === "member",
    isViewer: user?.role === "viewer",
  };
}

// ============================================================================
// HOC (Higher-Order Component) for easier usage
// ============================================================================

export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<ProtectedRouteProps, 'children'>
) {
  return function AuthenticatedComponent(props: P) {
    return (
      <ProtectedRoute {...options}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}
